import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {useField} from 'formik';
import classnames from 'classnames';
import ModalContent from '../ModalContent';
import ModalButtonsContainer from '../ModalButtonsContainer';
import ButtonAction from '../ButtonAction';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import DNDFileUpload from '../DNDFileUpload';
import Unit from '../Unit';
import {permissionTypes as T, subjects as S} from '../../constants/permissionSubjects';
import {buttonCancel, buttonSubmit} from '../../styles/styles';
import {can} from '../Permissions';
import Spinner from '../Spinner';
import {PopupUploadFilesProvider, usePopupUploadFilesContext} from './PopupUploadFilesContext';
import styles from './styles.module.scss';

interface ItemFileProps {
  id: string,
  label: string
}

interface PopupUploadFilesComponentProps {onClose: () => void;}

interface PopupUploadFilesProps {onUploadFile: (file: File[], fileTyeID: number) => Promise<void>;}

const ItemFileType = ({id, label}: ItemFileProps) => {
  const [field, /*meta*/, helper] = useField<number>('fileTypeID');

  return (
    <label className={classnames(styles.uploadItem, {[styles.active]: parseInt(id, 10) === field.value})}>
      <span className={'text-dark mb-0 h3'}>{label}</span>
      <input
        type={'radio'}
        hidden
        name={'fileTypeID'}
        id={id}
        value={id}
        onChange={async () => {
          await helper.setValue(parseInt(id, 10));
        }}
        multiple
      />
    </label>
  );
};

const PopupUploadFilesComponent = ({onClose}: PopupUploadFilesComponentProps) => {
  const intl = useIntl();
  const {
    fileTypes,
    files,
    onFilesChange,
    onFilesEdit,
    submit,
    isValid,
    isUploadAllowed,
    multifile
  } = usePopupUploadFilesContext();

  return (
    <>
      <ModalHeader
        type={'complex'}
        icon={'/media/common/Upload_file.svg'}
        header={intl.formatMessage({id: 'upload-file', defaultMessage: 'Upload file'})}
      />
      <ModalContent>
        <div className={'d-flex justify-content-between'}>
          <Unit
            text={intl.formatMessage({id: 'tab-order-documents-header-fileTypes', defaultMessage: 'File types'})}
            customClassnames={'w-50 mx-2'}
            icon={'/media/common/List_orange.svg'}
          >
            <div>
              {fileTypes.length > 0
                ? (
                  <div className={styles.fileTypesContainer}>
                    {fileTypes.map(item => <ItemFileType id={item.value.toString()} label={item.label}/>)}
                  </div>
                )
                : <Spinner customContainerStyles={'d-flex justify-content-center'}/>
              }
            </div>
          </Unit>
          <Unit
            text={intl.formatMessage({id: 'tab-order-documents-header-dnd', defaultMessage: 'Document'})}
            customClassnames={'w-50 mx-2'}
            icon={'/media/common/Upload_secondary.svg'}
          >
            <DNDFileUpload
              onChanged={onFilesChange}
              onEdit={onFilesEdit}
              files={files}
              multiple={multifile}
              disabled={!isUploadAllowed}
            />
          </Unit>
        </div>
      </ModalContent>
      <ModalButtonsContainer>
        <ButtonAction
          variant={'cancel'}
          onClick={onClose}
          label={intl.formatMessage({id: 'button-cancel', defaultMessage: 'Cancel'})}
          customClassnames={buttonCancel}
          disabled={false}
        />
        <ButtonAction
          onClick={async () => {
            await submit();
            onClose();
          }}
          useIcon={'/media/common/Check-in-circle_white.svg'}
          label={intl.formatMessage({id: 'button-submit', defaultMessage: 'Submit'})}
          disabled={!isValid}
          customClassnames={buttonSubmit}
        />
      </ModalButtonsContainer>
    </>
  );
};

const PopupUploadFiles = ({onUploadFile}: PopupUploadFilesProps) => {
  const [show, setShow] = useState(false);
  const handleCloseModal = () => setShow(false);
  const handleShowModal = () => setShow(true);
  const intl = useIntl();

  return (
    <>
      <div className={'d-flex mx-4'}>
        <ButtonAction
          label={
            <div>
              <img src={'../../../media/common/Upload_white.svg'} alt="icon" className={'w-20px mr-2'}/>
              <span>{intl.formatMessage({id: 'upload-file', defaultMessage: 'Upload file'})}</span>
            </div>
          }
          disabled={!can(T.FULL, S.PRICE_QUOTES_PAGE)}
          onClick={handleShowModal}
          customClassnames={'w-225px'}
        />
      </div>
      <ModalContainer show={show} onHide={handleCloseModal} size={'xl'}>
        <PopupUploadFilesProvider onFileUpload={onUploadFile}>
          <PopupUploadFilesComponent onClose={handleCloseModal}/>
        </PopupUploadFilesProvider>
      </ModalContainer>
    </>
  );
};

export default PopupUploadFiles;